import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { Add, Delete } from "@mui/icons-material";
import { GetDeviceFieldsHistory, UpdateDeviceFields } from "../Api";

interface Props {
  selectedDevice: {
    deviceID: string;
    name?: string;
  };
}

export interface FieldType {
  name: string;
  inputType: string;
  outputType: string;
  clickhouseColumn: string;
  factor: number;
  isArray: boolean;
  arraySize: number;
}

export interface SensorConfig {
  sensor_id: string;
  fields: FieldType[];
}

const FieldsConfiguration: React.FC<Props> = ({ selectedDevice }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const inputFieldTypes = [
    "uint8", "int8", "uint16", "int16", "uint32", "int32",
    "uint64", "int64", "float32", "float64",
  ];
  const outputFieldTypes = ["datetime", "float32", "int32", "int8", "uint32"];

  const [sensorConfigs, setSensorConfigs] = useState<SensorConfig[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  /**
   * Validate field name (no spaces)
   */
  const hasSpaces = (str: string) => /\s/.test(str);

  /**
   * Add a new Device Number (Sensor ID)
   */
  const addSensor = () => {
    const nextId = sensorConfigs.length;
    setSensorConfigs([
      ...sensorConfigs,
      {
        sensor_id: String(nextId),
        fields: [],
      },
    ]);
  };

  /**
   * Remove sensor by index
   */
  const removeSensor = (index: number) => {
    const updated = [...sensorConfigs];
    updated.splice(index, 1);
    setSensorConfigs(updated);
  };

  /**
   * Add a field to a specific sensor
   */
  const addFieldToSensor = (sensorIndex: number) => {
    const updated = [...sensorConfigs];
    updated[sensorIndex].fields.push({
      name: "",
      inputType: "uint8",
      outputType: "float32",
      clickhouseColumn: "",
      factor: 1,
      isArray: false,
      arraySize: 1,
    });
    setSensorConfigs(updated);
  };

  /**
   * Remove field from a sensor
   */
  const removeFieldFromSensor = (sensorIndex: number, fieldIndex: number) => {
    const updated = [...sensorConfigs];
    updated[sensorIndex].fields.splice(fieldIndex, 1);
    setSensorConfigs(updated);
  };

  /**
   * Update field value
   */
  const updateField = (
    sensorIndex: number,
    fieldIndex: number,
    key: keyof FieldType,
    value: string | number | boolean
  ) => {
    const updated = [...sensorConfigs];
    updated[sensorIndex].fields[fieldIndex][key] = value as never;
    setSensorConfigs(updated);
  };

  /**
   * Validate all fields
   */
  const validateAllFields = () => {
    for (let i = 0; i < sensorConfigs.length; i++) {
      const sensor = sensorConfigs[i];
      for (let j = 0; j < sensor.fields.length; j++) {
        const field = sensor.fields[j];
        if (!field.name)
          return `Field name required in Device Number (Sensor ID) ${sensor.sensor_id}`;
        if (hasSpaces(field.name))
          return `No spaces allowed in field name "${field.name}" in Sensor ID ${sensor.sensor_id}`;
      }
    }
    return null;
  };

  /**
   * Submit handler
   */
  const handleSubmit = async () => {
    const errorMsg = validateAllFields();
    if (errorMsg) {
      setError(errorMsg);
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      const existing = await GetDeviceFieldsHistory(selectedDevice.deviceID);
      let mergedData = { device_id: selectedDevice.deviceID, data: [] as SensorConfig[] };

      if (Array.isArray(existing?.data) && existing.data.length > 0) {
        const existingData = existing.data as SensorConfig[];
        const map = new Map<string, SensorConfig>();

        existingData.forEach((sensor) => map.set(sensor.sensor_id, { ...sensor }));

        sensorConfigs.forEach((newSensor) => {
          if (map.has(newSensor.sensor_id)) {
            const existingSensor = map.get(newSensor.sensor_id)!;
            const combinedFields = [...existingSensor.fields, ...newSensor.fields];
            const uniqueFieldsMap = new Map<string, FieldType>();
            combinedFields.forEach((f) => uniqueFieldsMap.set(f.name, f));
            existingSensor.fields = Array.from(uniqueFieldsMap.values());
            map.set(newSensor.sensor_id, existingSensor);
          } else {
            map.set(newSensor.sensor_id, newSensor);
          }
        });

        mergedData.data = Array.from(map.values());
      } else {
        mergedData.data = sensorConfigs;
      }

      await UpdateDeviceFields(selectedDevice.deviceID, mergedData);
      setSuccessMsg("Fields updated successfully");
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      console.error("Submission failed:", err);
      setError("Submission failed, something went wrong");
      setTimeout(() => setError(null), 3000);
    }
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      <Stack spacing={3}>
        <Alert severity="info">
          Each device can have multiple fields. Field named exactly <b>Time</b> is mandatory for timestamp.
        </Alert>

        {sensorConfigs.map((sensor, sIdx) => (
          <Paper key={sIdx} sx={{ p: isMobile ? 1 : 2 }}>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">
                  Device Number (Sensor ID): {sensor.sensor_id}
                </Typography>
                <IconButton color="error" onClick={() => removeSensor(sIdx)}>
                  <Delete />
                </IconButton>
              </Box>

              {sensor.fields.map((field, fIdx) => (
                <Paper
                  key={fIdx}
                  sx={{
                    p: isMobile ? 1 : 2,
                    backgroundColor: "#f9f9f9",
                    mt: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Field #{fIdx + 1}</Typography>
                      <IconButton
                        onClick={() => removeFieldFromSensor(sIdx, fIdx)}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Box>

                    <TextField
                      label="Field Name"
                      value={field.name}
                      onChange={(e) =>
                        updateField(sIdx, fIdx, "name", e.target.value.replace(/\s/g, ""))
                      }
                      fullWidth
                      error={hasSpaces(field.name)}
                      helperText={hasSpaces(field.name) ? "No spaces allowed" : ""}
                    />

                    <FormControl fullWidth>
                      <InputLabel>Input Type</InputLabel>
                      <Select
                        value={field.inputType}
                        onChange={(e) =>
                          updateField(sIdx, fIdx, "inputType", e.target.value)
                        }
                        label="Input Type"
                      >
                        {inputFieldTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Output Type</InputLabel>
                      <Select
                        value={field.outputType}
                        onChange={(e) =>
                          updateField(sIdx, fIdx, "outputType", e.target.value)
                        }
                        label="Output Type"
                      >
                        {outputFieldTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label="ClickHouse Column"
                      value={field.clickhouseColumn}
                      onChange={(e) =>
                        updateField(sIdx, fIdx, "clickhouseColumn", e.target.value)
                      }
                      fullWidth
                    />

                    <TextField
                      label="Factor"
                      type="number"
                      value={field.factor}
                      onChange={(e) =>
                        updateField(sIdx, fIdx, "factor", parseFloat(e.target.value))
                      }
                      fullWidth
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.isArray}
                          onChange={(e) =>
                            updateField(sIdx, fIdx, "isArray", e.target.checked)
                          }
                        />
                      }
                      label="Is Array"
                    />

                    {field.isArray && (
                      <TextField
                        label="Array Size"
                        type="number"
                        value={field.arraySize}
                        onChange={(e) =>
                          updateField(sIdx, fIdx, "arraySize", parseInt(e.target.value))
                        }
                        fullWidth
                      />
                    )}
                  </Stack>
                </Paper>
              ))}

              <Button
                startIcon={<Add />}
                onClick={() => addFieldToSensor(sIdx)}
                variant="outlined"
              >
                Add Field
              </Button>
            </Stack>
          </Paper>
        ))}

        <Box display={"flex"} gap={"0.5rem"} justifyContent={"center"} flexDirection={isMobile ? "column" : "row"}>
          <Button startIcon={<Add />} onClick={addSensor} variant="contained">
            Add New Device Number
          </Button>

          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
      </Stack>
    </>
  );
};

export default FieldsConfiguration;
